import { useCallback, useEffect, useState } from "react";
import { fetchLogs } from "../lib/api";

/**
 * Polls /api/v1/logs every `intervalMs` while `enabled && !paused`.
 * Re-pulls immediately whenever the level / query filters change.
 *
 * @param {Object}  options
 * @param {boolean} options.enabled
 * @param {string}  options.level       e.g. "ALL", "INFO", "WARNING"
 * @param {string}  options.query       case-insensitive substring filter
 * @param {boolean} options.paused
 * @param {number}  options.intervalMs  polling interval (default 2000)
 * @returns {{ records, error, refresh, clear }}
 */
export function useLogTail({
  enabled,
  level,
  query,
  paused,
  intervalMs = 2000,
}) {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await fetchLogs({
        limit: 400,
        level: level === "ALL" ? undefined : level,
        contains: query || undefined,
      });
      setRecords(data.records);
      setError(null);
    } catch (e) {
      setError(e);
    }
  }, [level, query]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    if (paused) return undefined;
    const id = setInterval(refresh, intervalMs);
    return () => clearInterval(id);
  }, [enabled, paused, intervalMs, refresh]);

  const clear = useCallback(() => setRecords([]), []);

  return { records, error, refresh, clear };
}
